import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth-service';
import { GET_ANONYMOUS_TOKEN_ROUTE } from '@storees/shared-constants';
import {
  AsyncTimeoutError,
  AUTH_REFRESH_INTERCEPTOR_JWT_SYNC_TIMEOUT_MS,
  promiseWithTimeout,
} from '../utils/async-timeout';

let isRefreshing = false;

export const authRefreshInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  return next(request).pipe(
    catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
      if (error.status === 401 && !isRefreshing) {
        if (request.url.includes(`/v1${GET_ANONYMOUS_TOKEN_ROUTE}`)) {
          return throwError(() => error);
        }

        isRefreshing = true;
        console.log('AuthRefreshInterceptor: 401 detected, fetching replacement token');

        return from(refreshTokenAndRetry(authService, request, next)).pipe(
          switchMap((event) => from([event as HttpEvent<unknown>])),
          catchError((refreshError) => {
            console.error(
              'AuthRefreshInterceptor: Failed to refresh token',
              refreshError
            );
            return throwError(() => refreshError);
          })
        );
      }
      return throwError(() => error);
    })
  );
};

async function refreshTokenAndRetry(
  authService: AuthService,
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Promise<HttpEvent<unknown>> {
  try {
    try {
      await promiseWithTimeout(
        authService.updateSessionJWT(),
        AUTH_REFRESH_INTERCEPTOR_JWT_SYNC_TIMEOUT_MS,
        'AuthRefreshInterceptor.updateSessionJWT'
      );
    } catch (e: unknown) {
      if (e instanceof AsyncTimeoutError) {
        console.error(
          'AuthRefreshInterceptor: JWT refresh timed out; subsequent API calls may fail until reload or login.'
        );
      }
      throw e;
    }

    const newToken = await authService.getSessionJwtToken();

    console.log('AuthRefreshInterceptor: New token obtained, retrying request');

    const clonedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${newToken}`,
      },
    });

    return await new Promise<HttpEvent<unknown>>((resolve, reject) => {
      next(clonedRequest).subscribe({
        next: (event) => resolve(event),
        error: (err) => reject(err),
      });
    });
  } finally {
    isRefreshing = false;
  }
}
